import {z} from 'zod';

import {CourseFeatureSchema} from './courseFeatures';
import {OfficeHoursConfigSchema} from './courseOfficeHours';
import {RoleFirebaseStringSchema} from './role';

// ============================================================================
// Course Document Types
// ============================================================================

/**
 * Shape of the course doc's style-feedback exclusion list. Shared by
 * {@link CourseDocumentSchema} and {@link isStyleFeedbackAvailableForAssignment}
 * so the field's definition and the code that reads it cannot drift apart.
 */
const StyleFeedbackExcludedAssignmentsSchema = z.array(z.string());

/**
 * Default for `CourseDocument.sectionFeedbackDelayDays`: SL section feedback
 * opens one day after the week's last section ends.
 */
export const SECTION_FEEDBACK_DELAY_DAYS_DEFAULT = 1;

/**
 * Default for `CourseDocument.studentJoinGraceMinutesAfterStart`: how many
 * minutes after a section starts a student may still join it for the first
 * time.
 */
export const STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT = 20;

/**
 * Default for `CourseDocument.studentDayBeforeReminderWeeks`: students get the
 * day-before section reminder before their first section only.
 */
export const STUDENT_DAY_BEFORE_REMINDER_WEEKS_DEFAULT: readonly number[] = [1];

/**
 * One stored entry of `studentDayBeforeReminderWeeks`: a 1-based course week,
 * as a number or as a string of digits.
 */
const StudentDayBeforeReminderWeekSchema = z
  .union([z.number(), z.string().regex(/^\s*\d+\s*$/)])
  .transform(Number)
  .pipe(z.number().int().min(1));

/**
 * Default for `CourseDocument.targetLiveSectionSize`: the reroute pickers'
 * soft cap from before the target was configurable, so courses that do not
 * set the field fill rooms in the same order as before.
 */
export const TARGET_LIVE_SECTION_SIZE_DEFAULT = 15;

/**
 * Programming tools in the shared rich text editor toolbar that a course can
 * hide, in the order the admin editor lists them. Each key names one
 * toolbar button.
 */
export const HIDEABLE_RICH_TEXT_TOOLS = [
  'codeBlock',
  'runnableCode',
  'karel',
  'graphics',
] as const;

/** One of the {@link HIDEABLE_RICH_TEXT_TOOLS}. */
export type HideableRichTextTool = (typeof HIDEABLE_RICH_TEXT_TOOLS)[number];

/**
 * The tools hidden on a course that has not set `hiddenRichTextTools`. No
 * course uses Karel, and its button was commented out before this field
 * existed, so an unset course keeps the toolbar it already had.
 */
export const DEFAULT_HIDDEN_RICH_TEXT_TOOLS: readonly HideableRichTextTool[] = [
  'karel',
];

/**
 * Shape of the course doc's `hiddenRichTextTools` field. Entries are plain
 * strings so a key added by a newer client doesn't fail validation of the
 * whole doc; {@link resolveHiddenRichTextTools} drops keys it doesn't know.
 */
const HiddenRichTextToolsSchema = z.array(z.string());

/**
 * Home-page calendar settings stored on the course document. Every field is
 * optional; read through {@link resolveCourseCalendarConfig}, which applies
 * the defaults.
 */
export const CourseCalendarConfigSchema = z
  .object({
    /**
     * When the first week's materials are released, as an ISO 8601 string
     * with a UTC offset (YYYY-MM-DDTHH:mmXXX). Later weeks repeat at the same
     * wall-clock time in the course `timezone`. Absent falls back to
     * `startDate`. Each learner's calendar week starts on the local day this
     * instant falls on.
     */
    firstReleaseTimestamp: z.string().optional(),
    /** Show a timed event at each week's materials release. Default on. */
    showMaterialsRelease: z.boolean().optional(),
    /** Show the "complete this week's tasks" banner. Default on. */
    showTasksBanner: z.boolean().optional(),
    /** Show the viewer's weekly section meeting. Default on. */
    showSections: z.boolean().optional(),
  })
  .loose();

export type CourseCalendarConfig = z.infer<typeof CourseCalendarConfigSchema>;

/** {@link CourseCalendarConfig} with every default applied. */
export interface ResolvedCourseCalendarConfig {
  /**
   * First release as an ISO 8601 string with offset, or an empty string
   * when neither the calendar setting nor `startDate` is set.
   */
  firstReleaseTimestamp: string;
  /** Whether to show the weekly materials-release event. */
  showMaterialsRelease: boolean;
  /** Whether to show the weekly tasks banner. */
  showTasksBanner: boolean;
  /** Whether to show the viewer's section meetings. */
  showSections: boolean;
}

/**
 * Default for `CourseDocument.shadowReviewDueWeekday`: peer-shadow reviews are
 * due at the end of Sunday. Weekdays are numbered as `Date.prototype.getDay`
 * numbers them.
 */
export const SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT = 0;

/**
 * Course pages staff can lock until they are ready, in the order the admin
 * editor lists them. Each key is the page's route segment, except `forum`,
 * which covers the main forum.
 */
export const LOCKABLE_COURSE_PAGES = [
  'studenthome',
  'learn',
  'forum',
  'shadowing',
] as const;

/** One of the {@link LOCKABLE_COURSE_PAGES}. */
export type LockableCoursePage = (typeof LOCKABLE_COURSE_PAGES)[number];

/**
 * Shape of the course doc's `lockedPages` field. Entries are plain strings so
 * a key added by a newer client doesn't fail validation of the whole doc;
 * {@link resolveLockedPages} drops keys it doesn't know.
 */
const LockedPagesSchema = z.array(z.string());

/**
 * A lesson that lives in another course but is listed on this course's
 * Lessons page. Read via {@link resolveLinkedLessons}.
 */
const LinkedLessonSchema = z.object({
  /** The course that owns the lesson, e.g. `public`. */
  courseId: z.string().min(1),
  /** The lesson's document id in that course's `lessonsList`. */
  lessonId: z.string().min(1),
});

/** Shape of the course doc's `linkedLessons` field. */
const LinkedLessonsSchema = z.array(LinkedLessonSchema);

/** One entry of a course document's `linkedLessons`. */
export type LinkedLesson = z.infer<typeof LinkedLessonSchema>;

/**
 * Base schema for course documents.
 *
 * This schema intentionally uses primitive types (string/number/boolean) for
 * fields like language codes and feature flags to avoid depending on
 * app-specific enums. Callers can narrow these fields to more specific types
 * in their own modules if needed.
 */
export const CourseDocumentSchema = z
  .object({
    /**
     * The (human-readable) name of the course.
     */
    name: z.string().optional(),

    /**
     * A longer description of the course.
     */
    description: z.string().optional(),

    /**
     * Alternate names / keywords for the course (e.g. "CiP 2026", "Code in
     * Place '26"). Used by the inbound email agent's classifier to match a
     * course mentioned in an email back to this course's id, alongside `name`.
     */
    aliases: z.array(z.string()).optional(),

    /**
     * The minimum role required to access the course.
     */
    minRole: RoleFirebaseStringSchema.optional(),

    /**
     * The end date of the course. Formatted as YYYY-MM-DD.
     */
    endDate: z.string().optional(),

    /**
     * The features enabled for the course.
     */
    features: z.array(CourseFeatureSchema).optional(),

    /**
     * The features enabled for the section leaders.
     */
    slFeatures: z.array(CourseFeatureSchema).optional(),

    /**
     * Features that show a Beta badge, on their sidebar entry and page
     * title, to everyone who sees them. Independent of whether the feature
     * is enabled; absent means no feature is marked beta.
     */
    betaFeatures: z.array(CourseFeatureSchema).optional(),

    /**
     * Whether the section leader application is currently open.
     */
    slAppOpen: z.boolean().optional(),

    /**
     * Whether SL onboarding requires a backup weekly section time.
     */
    requireSlBackupSectionTime: z.boolean().optional(),

    /**
     * Whether the student application is open.
     */
    studentAppOpen: z.boolean().optional(),

    /**
     * Whether this course's application flows are served at the short link
     * (`/apply/{courseId}/student` and `/apply/{courseId}/sl`) as their
     * canonical, persistent URL. When enabled, the long-form splash routes
     * (`/{parentCourseId}/join/{courseId}` and
     * `/{parentCourseId}/applyteach/{courseId}`) redirect to the short link;
     * when absent or false, the short link redirects to the long-form routes
     * instead (the legacy behavior). Opt-in: absent is treated as disabled.
     * Courses whose parent course is PAI always use the short link,
     * regardless of this flag.
     */
    applyShortLinkEnabled: z.boolean().optional(),

    /**
     * Whether the student application results are live.
     */
    studentAppDecisionsLive: z.boolean().optional(),

    /**
     * Whether every student who finishes the application is admitted. When
     * set and `studentAppDecisionsLive` is on, a finished student application
     * counts as an admission whether or not the applicant holds a course role
     * yet. Absent or false: only the `student` role admits.
     */
    studentAppAdmitsAllFinished: z.boolean().optional(),

    /**
     * The start date of the course. Formatted as an ISO 8601 string as
     * YYYY-MM-DDTHH:mm:ssXXX
     */
    startDate: z.string().optional(),

    /**
     * The timestamp of the first section of the course. Section start times are
     * calculated from this timestamp and the section time delta. Note that the
     * timezone of this field may not be specified and is hardcoded to UTC-7
     * (PDT). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ss.
     */
    firstSectionTimestamp: z.string().optional(),

    /**
     * The due date of the student application. Formatted as an ISO 8601 string
     * as YYYY-MM-DDTHH:mm:ssXXX
     */
    studentAppDueDate: z.string().optional(),

    /**
     * The previous due date of the student application. Useful for nicely
     * displaying extensions to the due date. Formatted as an ISO 8601 string as
     * YYYY-MM-DDTHH:mm:ssXXX
     */
    studentResultsDate: z.string().optional(),

    /**
     * The due date of the section leader application. Formatted as an ISO 8601
     * string as YYYY-MM-DDTHH:mm:ssXXX
     */
    slAppDueDate: z.string().optional(),

    /**
     * The previous due date of the section leader application. Useful for
     * nicely displaying extensions to the due date. Formatted as an ISO 8601
     * string as YYYY-MM-DDTHH:mm:ssXXX
     */
    slAppPrevDueDate: z.string().optional(),

    /**
     * Manual early release of section leader application results. When true,
     * results are released regardless of `slResultsDate`; when absent or
     * false they release at `slResultsDate`.
     */
    slAppDecisionsLive: z.boolean().optional(),

    /**
     * The instant section leader application results are released: the
     * admit/reject letter, confetti and the home-page course card stay hidden
     * before it (the interval `[slResultsDate, +inf)` is released). Also the
     * "come back on" date shown to applicants. Formatted as an ISO 8601 string
     * as YYYY-MM-DDTHH:mm:ssXXX. See `slAppDecisionsLive` for the
     * early-release override, and `slOnboardingStartDateTimestamp` for when
     * onboarding opens.
     */
    slResultsDate: z.string().optional(),

    /**
     * The date by which section leaders must confirm their enrollment. Formatted
     * as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX
     */
    slConfirmDate: z.string().optional(),

    /**
     * The timezone of the course. Represented as an IANA timezone string.
     */
    timezone: z.string().optional(),

    /**
     * Whether section times keep their wall-clock time in `timezone` across
     * that zone's DST changes (Tuesday 11:00 stays Tuesday 11:00 local), rather
     * than recurring at a fixed UTC instant. Absent or false keeps the
     * fixed-instant behavior. Resolve it through `getSectionClock` and pass the
     * clock to `computeSectionInstant`; with the flag on and `timezone` missing
     * or invalid, section math falls back to fixed instants.
     */
    sectionTimesFollowCourseTimezone: z.boolean().optional(),

    /**
     * The number of weeks in the course.
     */
    nWeeks: z.number().optional(),

    /**
     * When true, the hourly section reminder emails skip this course
     * entirely. Omitted or false sends them as usual.
     */
    sectionReminderEmailsPaused: z.boolean().optional(),

    /**
     * The 1-based course weeks in which students get the reminder sent a day
     * before their section. Entries may be stored as numeric strings. Absent
     * falls back to {@link STUDENT_DAY_BEFORE_REMINDER_WEEKS_DEFAULT}; an empty
     * list sends it in no week. Section leaders get that reminder every week
     * regardless. Read via {@link resolveStudentDayBeforeReminderWeeks}, which
     * also handles a malformed value; it is left unchecked here so one bad
     * entry cannot fail every parse of the course document.
     */
    studentDayBeforeReminderWeeks: z.unknown().optional(),

    /**
     * TODO: Remove this field. Only used by CS 105 to determine which
     * instructors are in the course. Instead, instructors should be fetched
     * from a new field.
     */
    isCodeInPlaceCourse: z.boolean().optional(),

    /**
     * Whether `/{courseId}/aboutcourse` renders the per-role expectations
     * syllabus (an expectations card, or tab, per role) rather than the plain
     * about-course handout on its own.
     *
     * Deliberately three-state. `true` enables it, `false` disables it, and an
     * absent field falls back to the courseId heuristic that decided this
     * before the flag existed (`hasAboutCourseExpectations` in the webapp), so
     * cohorts that predate the flag keep their expectations page untouched. Do
     * not read this field directly; resolve it through the webapp's
     * `resolveHasAboutCourseExpectations` so the fallback is applied.
     */
    aboutCourseExpectationsEnabled: z.boolean().optional(),

    /**
     * The number of hours of section a week. In other words, the difference in
     * hours between the start of the first section and the start of the last
     * section in a week.
     */
    sectionTimeDelta: z.union([z.string(), z.number()]),

    /**
     * Days after the week's last section ENDS before SL section feedback opens.
     * Absent ⇒ `SECTION_FEEDBACK_DELAY_DAYS_DEFAULT`.
     */
    sectionFeedbackDelayDays: z.number().optional(),

    /**
     * Minutes after a section's start during which a student who has not
     * joined it yet may still join. The window is half-open
     * `[start, start + N)`: at exactly N minutes a first join is refused.
     * Students who already joined can rejoin until the room closes. Absent ⇒
     * `STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT`.
     */
    studentJoinGraceMinutesAfterStart: z.number().optional(),

    /**
     * How many students (not counting the leader) the reroute and makeup
     * pickers aim for in a live section. Rooms with fewer are filled first,
     * weighted by open seats; rooms at or above it are used only once every
     * room has reached it. Absent ⇒ `TARGET_LIVE_SECTION_SIZE_DEFAULT`.
     */
    targetLiveSectionSize: z.number().optional(),

    /**
     * The weekday at whose end, Anywhere on Earth, a week's peer-shadow reviews
     * are due (see `getShadowReviewDueMs`), numbered as `Date.prototype.getDay`
     * numbers weekdays. Absent ⇒ `SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT`. Not
     * range-checked here: the deadline math wraps an out-of-range value, and a
     * stray value must not fail the whole course doc for unrelated readers.
     */
    shadowReviewDueWeekday: z.number().optional(),

    /**
     * The typesense index for the stories feature.
     */
    storiesTypesenseIndex: z.string().optional(),

    /**
     * The link to use to authenticate with Canvas.
     */
    canvasAuthLink: z.string().optional(),

    /**
     * Whether the section leader onboarding is closed.
     */
    isSLOnboardingClosed: z.boolean().optional(),

    /**
     * The roadmap list for the course. This is the set of roles that have
     * access to a roadmap for the course.
     */
    roadmapList: z.array(RoleFirebaseStringSchema).optional(),

    /**
     * Whether Karel is used in the course.
     */
    usesKarel: z.boolean().optional(),

    /**
     * Whether TeachNow is used in the course.
     */
    usesTeachNow: z.boolean().optional(),

    /**
     * Whether sections in this course use an instructor-provided ("bring your
     * own") Zoom link instead of CIP-managed meeting creation. When true, the
     * section join surface opens the section's `zoomLink` directly rather than
     * routing through the CIP-managed `createZoomMeeting` flow, and the link
     * becomes editable in the section management UI. Independent of where
     * attendance is recorded — Canvas-backed courses keep their Canvas grade
     * sheet. When absent, treated as false (CIP-managed Zoom). See the
     * external-Zoom join path in the webapp section page.
     */
    usesExternalZoom: z.boolean().optional(),

    /**
     * Whether the course teaches section from slide decks: the section page
     * shows a Slides column, and a section leader's Join Section button also
     * opens the week's deck (the guide config's `deckId`) in a new tab before
     * the Zoom redirect. Weeks with no deck join as usual. When absent,
     * treated as false.
     */
    usesSlidesForSection: z.boolean().optional(),

    /**
     * Previous name of `usesSlidesForSection`, read as a fallback so courses
     * whose document still carries it keep the behavior. Written only by
     * courses configured before the rename; the admin editor writes the new
     * field.
     *
     * @deprecated Use `usesSlidesForSection`.
     */
    opensSlidesOnSectionJoin: z.boolean().optional(),

    /**
     * Whether the section page shows the weekly section leader Guide column,
     * which links to `/{courseId}/section/{week}/guide`. The guide reads the
     * week's `guideConfig`, the `week-{N}` roadmap modules and the
     * `guideWeeks` doc, so courses that do not keep those read as empty. When
     * absent, treated as true.
     */
    usesSectionGuide: z.boolean().optional(),

    /**
     * Whether the section page shows students a Reflection column, linking to
     * the week's reflection lesson in its `week-{N}` roadmap module. Courses
     * whose roadmap modules are not named that way have no reflection lesson
     * to link. When absent, treated as true.
     */
    usesSectionReflection: z.boolean().optional(),

    /**
     * Whether the section page shows a Handout column, linking to
     * `/{courseId}/section/{week}/handout`. When absent, treated as false.
     */
    usesSectionHandout: z.boolean().optional(),

    /**
     * Whether the section page shows a Key column, linking to the week's
     * solution at `/{courseId}/section/{week}/solution`. When absent, treated
     * as false.
     */
    usesSectionSolution: z.boolean().optional(),

    /**
     * Whether the section page shows section leaders a Lesson Plan column,
     * linking to `/{courseId}/section/{week}/lessonplan`, which renders the
     * week's chapter of the course's published `teacherhandbook` textbook.
     * When absent, treated as false.
     */
    usesSectionLessonPlan: z.boolean().optional(),

    /**
     * Whether the course teaches section with twists. When off, the SL debrief
     * drops its per-student twist rating column and the roster heading reads
     * "Participation" alone. Ratings already stored on past debriefs are
     * untouched. When absent, treated as true.
     */
    usesSectionTwists: z.boolean().optional(),

    /**
     * Firestore ids of this course's Super section records. Missing means
     * Super sections are disabled; an array, including an empty array, means
     * they are enabled.
     */
    superSectionIds: z.array(z.string()).optional(),

    /**
     * UIDs of the course's Super Section leaders, shared by every meeting
     * time. Being listed grants nothing by itself: every check that admits a
     * rostered leader also requires a section-leader-or-higher course role at
     * that moment, so an entry for someone whose role later changes is inert.
     * Missing means no leaders.
     */
    superSectionLeaderIds: z.array(z.string()).optional(),

    /**
     * The time at which the TeachNow "Start Teaching" flow becomes available
     * (e.g. for students who were previously shown a coming-soon message).
     * When absent, the webapp may fall back to legacy copy or treat teaching as
     * unrestricted depending on the surface. Formatted as an ISO 8601 string as
     * YYYY-MM-DDTHH:mm:ssXXX.
     */
    teachNowStartTimestamp: z.string().optional(),

    /**
     * The time at which TeachNow ends for the course. Once the current time is
     * at or after this timestamp, every "Start TeachNow" entry point is replaced
     * with a "TeachNow for {course} has ended" notice (the surrounding pages,
     * including the teacher's impact/session history, keep rendering). The
     * availability window is half-open `[teachNowStartTimestamp,
     * teachNowEndTimestamp)`: available at/after start, ended at/after end. When
     * absent, TeachNow has no scheduled end and stays available. Formatted as an
     * ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX.
     */
    teachNowEndTimestamp: z.string().optional(),

    /**
     * The number of students allowed to be permanently assigned to a non-minor
     * section.
     */
    nonMinorSectionCap: z.number().optional(),

    /**
     * The number of students allowed to be permanently assigned to a minor
     * section.
     */
    minorSectionCap: z.number().optional(),

    /**
     * Whether the course runs sections for minors. When `false`, the course
     * has no minor sections, so applicants who are minors on the course
     * `startDate` are left out of section leader application review. Omitted
     * means `true`.
     */
    hasMinorSections: z.boolean().optional(),

    /**
     * The default language for the course. Overrides the user's preferred
     * language if they navigate to the course in a language other than the
     * default.
     */
    // TODO: use LanguageCode type when it is in core
    defaultLanguage: z.string().optional(),

    /**
     * Availability of each hour slot within a section week for SL signup. Array
     * length should equal sectionTimeDelta. Each boolean indicates if that hour
     * offset from firstSectionTimestamp is available (true) or not (false). If
     * not specified, all slots are available.
     */
    sectionLeaderSectionSignupAvailability: z.array(z.boolean()).optional(),

    /**
     * The number of weeks between the first and second section leader training
     * sessions. Training sessions are expected to happen on the same day and
     * time each week.
     */
    numWeeksBetweenTrainingSessions: z.number().optional(),

    /**
     * Whether SL onboarding asks section leaders to choose a live section
     * practice time with a Head TA. When `false`, the step is hidden and not
     * required to finish onboarding, and the SL training page has no Head TA
     * profile card. Absent ⇒ true.
     */
    hasSlLivePractice: z.boolean().optional(),

    /**
     * The timestamp of the end of the section leader onboarding (prior to
     * training). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX
     */
    slOnboardingEndDateTimestamp: z.string().optional(),

    /**
     * When section leader onboarding opens, as an ISO 8601 string with a UTC
     * offset (YYYY-MM-DDTHH:mmXXX). Before it, section leaders see when
     * onboarding opens instead of its steps and cannot pick a section time;
     * the instant itself is open. Independent of `slResultsDate`. Absent:
     * onboarding has no lower bound.
     */
    slOnboardingStartDateTimestamp: z.string().optional(),

    /**
     * When mentor onboarding opens, as an ISO 8601 string with a UTC offset
     * (YYYY-MM-DDTHH:mmXXX). Before it, mentors see when onboarding opens
     * instead of its steps; the instant itself is open. Independent of
     * `slResultsDate` and `slOnboardingStartDateTimestamp`. Absent: onboarding
     * has no lower bound.
     */
    mentorOnboardingStartDateTimestamp: z.string().optional(),

    /**
     * When mentor onboarding closes, as an ISO 8601 string with a UTC offset
     * (YYYY-MM-DDTHH:mmXXX). From this instant on, a mentor who has not
     * finished onboarding sees that it has closed and cannot finish; the
     * instant itself is closed. Mentors who already finished are unaffected.
     * Independent of `slOnboardingEndDateTimestamp`. Absent: onboarding has no
     * upper bound.
     */
    mentorOnboardingEndDateTimestamp: z.string().optional(),

    /**
     * Whether section leaders can refer students during onboarding. When
     * `false`, section leader onboarding has no student referral step. Omitted
     * means `true`.
     */
    allowSlsToReferStudents: z.boolean().optional(),

    /**
     * Whether section leaders (and higher roles) see student roadmap modules
     * before their release date, marked as upcoming, in the Challenges and
     * Lessons tabs. Off by default because some courses park hidden modules
     * at far-future release dates. Absent ⇒ false.
     */
    showUpcomingContentToSls: z.boolean().optional(),

    /**
     * Lessons from other courses to list first on this course's Lessons
     * page, in this order, along with their worked examples on the Practice
     * page. Each opens in its own course, so its progress and video
     * reactions stay there. Absent: none. Read via
     * {@link resolveLinkedLessons}. Typed loosely so a malformed value can't
     * fail validation of the whole course doc.
     */
    linkedLessons: z.unknown().optional(),

    /**
     * Toolbar buttons the rich text editor (every `DiscussionButtonBar`
     * editor in the course) leaves out, for courses where they don't apply.
     * Absent falls back to {@link DEFAULT_HIDDEN_RICH_TEXT_TOOLS}; an empty
     * array hides nothing. Gates authoring UI only; content already using a
     * hidden tool still renders. Read via
     * {@link resolveHiddenRichTextTools}.
     */
    hiddenRichTextTools: HiddenRichTextToolsSchema.optional(),

    /**
     * The timestamp of the end of the student onboarding. Formatted as an ISO
     * 8601 string as YYYY-MM-DDTHH:mm:ssXXX
     */
    studentOnboardingEndDateTimestamp: z.string().optional(),

    /**
     * When student onboarding opens, as an ISO 8601 string with a UTC offset
     * (YYYY-MM-DDTHH:mmXXX). When set, the student admission letter lists
     * the course's key dates and links to onboarding only once this instant
     * has passed, students cannot onboard or pick a section before it, and
     * SectionGate's phase 2 waits `SECTION_SIGNUP_V2_PHASE_TWO_HOLD_MS` after
     * it. Absent: the letter links to onboarding straight away, onboarding
     * has no lower bound, and SectionGate stays in phase 1.
     */
    studentOnboardingStartDateTimestamp: z.string().optional(),

    /**
     * The timestamp at which students will be matched to their sections for the
     * current course offering. Used on the student Section page to tell
     * students who are not yet in a section when their section will be
     * assigned. When this field is absent, the page falls back to the
     * default "not in a section yet" message. Formatted as an ISO 8601 string
     * as YYYY-MM-DDTHH:mm:ssXXX.
     */
    sectionMatchingTimestamp: z.string().optional(),

    /**
     * Whether students have been matched to their sections for the current
     * course offering. Set by admins once matching has run, so pages can tell
     * "matching is late" apart from "matching is done" when
     * `sectionMatchingTimestamp` has passed. Absent means not matched.
     */
    areSectionsMatched: z.boolean().optional(),

    /**
     * Whether the course is currently active. This is used to enable select
     * firebase trigger executions, permissions for certain features, etc.
     * The notion of isActive = true is that we still allow students, SLs, or
     * staff to use features of a course. This could extend well past the
     * endDate of a course.
     */
    isActive: z.boolean().optional(),

    /**
     * The parent course (e.g. public for cip courses, pylatino-publico for
     * pylatino courses).
     */
    parentCourseId: z.string().optional(),

    /**
     * The current course to advertise in links, primarily used in public course,
     * or other parent courses.
     */
    courseToAdvertise: z.string().optional(),

    /**
     * Default from email address to use for course emails when one is not
     * explicitly specified in a given email batch/template.
     */
    defaultFromEmailAddress: z.string().optional(),

    /**
     * Start of the window during which SLs/mentors may set the name printed on
     * their (off-platform, mailed) certificate via the teacher-certificate
     * flow. Inclusive lower bound. When absent, the window has no lower bound.
     * Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX.
     */
    teacherCertificateNameOpenTimestamp: z.string().optional(),

    /**
     * End of the teacher-certificate name-capture window. Inclusive upper
     * bound. When absent, the window has no upper bound. Admins typically set
     * this to "now" once certificates have been exported so SLs/mentors can no
     * longer change the name that was already mailed. Formatted as an ISO 8601
     * string as YYYY-MM-DDTHH:mm:ssXXX.
     */
    teacherCertificateNameCloseTimestamp: z.string().optional(),

    /**
     * Master switch for the SimTeach practice experience
     * (`/{courseId}/simteachpractice` + `/{courseId}/the-teaching-game`). A
     * standalone enable flag, NOT a sidebar {@link CourseFeatureSchema}
     * `features` entry. Opt-in: absent is treated as disabled.
     */
    simTeachPracticeEnabled: z.boolean().optional(),

    /**
     * Scheduled shutdown for SimTeach practice: at/after this instant only
     * admins and above can reach it. Absent means no scheduled shutdown.
     * Interpreted via {@link canAccessScheduledShutdownFeature}. ISO 8601.
     */
    simTeachPracticeEndTimestamp: z.string().optional(),

    /**
     * Master switch for the coding-buddy experience (`/{courseId}/buddy` + the
     * dashboard nudge). A standalone enable flag, NOT a sidebar
     * {@link CourseFeatureSchema} `features` entry. Opt-in: absent is treated
     * as disabled.
     */
    codingBuddyEnabled: z.boolean().optional(),

    /**
     * Master switch for TeachNow live help: students ask for help from the
     * IDE and section leaders and mentors answer. A standalone enable flag,
     * NOT a sidebar {@link CourseFeatureSchema} `features` entry. Also needs
     * `teachnow` in `slFeatures` and the TeachNow start/end window; see
     * `isLiveHelpOpen` in `@cip/core/teachnow`. Opt-in: absent is disabled.
     */
    teachNowLiveHelpEnabled: z.boolean().optional(),

    /**
     * Percent of students (0 to 100) who may ask for TeachNow live help, by
     * a fixed hash of their uid, so raising it only adds students. Absent
     * means the default; see `getLiveHelpStudentPercent` in
     * `@cip/core/teachnow`.
     */
    teachNowLiveHelpStudentPercent: z
      .number()
      .min(0)
      .max(100)
      .nullable()
      .optional(),

    /**
     * Uids of test accounts that may always ask for TeachNow live help,
     * whatever `teachNowLiveHelpStudentPercent` says; see
     * `isStudentInLiveHelpRollout` in `@cip/core/teachnow`.
     */
    teachNowLiveHelpTestStudentIds: z.array(z.string()).nullable().optional(),

    /**
     * Uids of the only helpers asked when a test account asks, of any role;
     * see `getLiveHelpTestHelperIds` in `@cip/core/teachnow`.
     */
    teachNowLiveHelpTestHelperIds: z.array(z.string()).nullable().optional(),

    /**
     * Uids of staff of any role asked for every student's live help request,
     * alongside the SLs; toggled from the live help dashboard. See
     * `getLiveHelpStaffHelperIds` in `@cip/core/teachnow`.
     */
    teachNowLiveHelpStaffHelperIds: z.array(z.string()).nullable().optional(),

    /**
     * Scheduled shutdown for coding buddy: at/after this instant only admins
     * and above can reach it. Absent means no scheduled shutdown. Interpreted
     * via {@link canAccessScheduledShutdownFeature}. ISO 8601.
     */
    codingBuddyEndTimestamp: z.string().optional(),

    /**
     * Master switch for timed comments + emoji reactions on lesson videos.
     * A standalone enable flag, NOT a sidebar {@link CourseFeatureSchema}
     * `features` entry. Opt-in: absent is disabled, so courses predating
     * the feature never surface it. Enforced in `firestore.rules` as well
     * as the UI. A video slide's `commentsEnabled` can only narrow this for
     * that slide (turn it off, or keep reactions while dropping comments),
     * never turn it on.
     */
    videoCommentsEnabled: z.boolean().optional(),

    /**
     * Opts the course into the course presence system
     * (`@cip/core/coursePresence`) in place of the legacy `{courseId}/status`
     * heartbeat. On its own it records nothing; each presence feature, such as
     * `onlineCountEnabled`, decides whether students check in. Opt-in: absent
     * keeps the legacy system.
     */
    coursePresenceEnabled: z.boolean().optional(),

    /**
     * Shows the "N online" count in the course sidebar, computed by the course
     * presence system. Has no effect unless `coursePresenceEnabled` is on.
     * Absent is treated as off.
     */
    onlineCountEnabled: z.boolean().optional(),

    /**
     * Assignment UIDs in this course that do not offer IDE style feedback,
     * normally the code problems in the section leader and student
     * applications. Karel problems need no entry; the IDE excludes those on
     * its own. Absent falls back to
     * {@link DEFAULT_STYLE_FEEDBACK_EXCLUDED_ASSIGNMENTS}; an empty array
     * excludes nothing. Read via
     * {@link isStyleFeedbackAvailableForAssignment}.
     */
    styleFeedbackExcludedAssignments:
      StyleFeedbackExcludedAssignmentsSchema.optional(),
    /**
     * Home-page calendar settings. Absent falls back to the defaults in
     * {@link resolveCourseCalendarConfig}. Whether the calendar shows at all
     * is the `calendar` entry in `features` / `slFeatures`.
     */
    calendar: CourseCalendarConfigSchema.optional(),

    /**
     * Pages that keep their sidebar entry but are closed for now: the entry
     * reads "Coming soon" and the route shows a notice instead of the page.
     * Staff can still open them. Absent means nothing is locked. Read via
     * {@link resolveLockedPages}.
     */
    lockedPages: LockedPagesSchema.optional(),

    /**
     * Office hours settings. Absent means the course has no office hours.
     * Read via `resolveOfficeHoursConfig` in `@cip/core/officeHours`.
     */
    officeHours: OfficeHoursConfigSchema.optional(),
  })
  .loose();

export type CourseDocument = z.infer<typeof CourseDocumentSchema>;

/**
 * Whether a string is one of the {@link LOCKABLE_COURSE_PAGES}.
 *
 * @param page - Any page key.
 * @returns True when `page` can be locked.
 * @throws Never.
 */
export function isLockableCoursePage(page: string): page is LockableCoursePage {
  return LOCKABLE_COURSE_PAGES.some(lockable => lockable === page);
}

/**
 * The pages a course has locked.
 *
 * @param args - Inputs.
 * @param args.course - The course document, or undefined while it loads. The
 *   field is typed `unknown` because the client's course converter reads
 *   without validating, so anything that is not a list of strings resolves to
 *   no locked pages.
 * @returns The locked pages, without unknown keys or repeats.
 * @throws Never.
 */
export function resolveLockedPages({
  course,
}: {
  course: {lockedPages?: unknown} | null | undefined;
}): readonly LockableCoursePage[] {
  const parsed = LockedPagesSchema.safeParse(course?.lockedPages);
  if (!parsed.success) {
    return [];
  }
  return LOCKABLE_COURSE_PAGES.filter(page => parsed.data.includes(page));
}

/**
 * The lessons from other courses that a course lists on its Lessons page.
 *
 * @param args - Inputs.
 * @param args.course - The course document, or undefined while it loads. The
 *   field is typed `unknown` because the client's course converter reads
 *   without validating, so anything that is not a valid list resolves to no
 *   lessons.
 * @returns The linked lessons in stored order.
 * @throws Never.
 */
export function resolveLinkedLessons({
  course,
}: {
  course: {linkedLessons?: unknown} | null | undefined;
}): readonly LinkedLesson[] {
  const parsed = LinkedLessonsSchema.safeParse(course?.linkedLessons);
  return parsed.success ? parsed.data : [];
}

/**
 * The home-page calendar settings that apply to a course.
 *
 * @param args - Inputs.
 * @param args.course - The course document, or undefined while it loads.
 *   `calendar` is typed `unknown` because the client's course converter reads
 *   without validating; anything that is not a calendar config resolves to
 *   the defaults.
 * @returns The settings with defaults applied. `firstReleaseTimestamp` falls
 *   back to `startDate`, then to an empty string.
 * @throws Never.
 */
export function resolveCourseCalendarConfig({
  course,
}: {
  course: {calendar?: unknown; startDate?: unknown} | null | undefined;
}): ResolvedCourseCalendarConfig {
  const parsed = CourseCalendarConfigSchema.safeParse(course?.calendar);
  const config = parsed.success ? parsed.data : {};
  const startDate =
    typeof course?.startDate === 'string' ? course.startDate : '';
  return {
    firstReleaseTimestamp: config.firstReleaseTimestamp || startDate,
    showMaterialsRelease: config.showMaterialsRelease ?? true,
    showTasksBanner: config.showTasksBanner ?? true,
    showSections: config.showSections ?? true,
  };
}

/**
 * Style-feedback exclusions for a course that does not set its own list.
 * These were hardcoded in the webapp IDE before the course-doc field existed,
 * so they remain the fallback to keep existing courses unchanged.
 */
export const DEFAULT_STYLE_FEEDBACK_EXCLUDED_ASSIGNMENTS: readonly string[] = [
  'warmup',
  'housekarel',
  'applyteach-fillkarel',
  'doubleit',
  'tpnondecreasing',
  'tpastorheight',
] as const;

/**
 * Whether the IDE should offer style feedback for one assignment.
 *
 * @param args - Inputs.
 * @param args.course - The course document, or undefined while it loads, which
 *   resolves to {@link DEFAULT_STYLE_FEEDBACK_EXCLUDED_ASSIGNMENTS}. The
 *   exclusion list is typed `unknown` because the client's course converter
 *   reads without validating (see `courseConverter` in
 *   firebase/client/course.ts), so it arrives in whatever shape the document
 *   actually holds; anything that is not an array of ids resolves to the same
 *   default.
 * @param args.assignmentId - The assignment's `metaData.uid`, or undefined when
 *   no assignment is loaded, which returns false.
 * @returns Whether style feedback is available for this assignment.
 * @throws Never.
 */
export function isStyleFeedbackAvailableForAssignment({
  course,
  assignmentId,
}: {
  course: {styleFeedbackExcludedAssignments?: unknown} | undefined;
  assignmentId: string | undefined;
}): boolean {
  if (!assignmentId) {
    return false;
  }
  // Parse rather than trust: a bare string here would turn `includes` into a
  // substring test, quietly excluding every assignment whose id appears inside
  // the stored value.
  const configured = StyleFeedbackExcludedAssignmentsSchema.safeParse(
    course?.styleFeedbackExcludedAssignments,
  );
  const excluded = configured.success
    ? configured.data
    : DEFAULT_STYLE_FEEDBACK_EXCLUDED_ASSIGNMENTS;
  return !excluded.includes(assignmentId);
}

/**
 * The rich text editor tools a course hides.
 *
 * @param args - Inputs.
 * @param args.course - The course document, or undefined while it loads. The
 *   field is typed `unknown` because the client's course converter reads
 *   without validating, so anything that is not a list of strings resolves to
 *   {@link DEFAULT_HIDDEN_RICH_TEXT_TOOLS}. A course that stores an empty
 *   list hides nothing, so a course can show every tool.
 * @returns The hidden tools, without unknown keys or repeats.
 * @throws Never.
 */
export function resolveHiddenRichTextTools({
  course,
}: {
  course: {hiddenRichTextTools?: unknown} | null | undefined;
}): readonly HideableRichTextTool[] {
  const parsed = HiddenRichTextToolsSchema.safeParse(
    course?.hiddenRichTextTools,
  );
  const hidden: readonly string[] = parsed.success
    ? parsed.data
    : DEFAULT_HIDDEN_RICH_TEXT_TOOLS;
  return HIDEABLE_RICH_TEXT_TOOLS.filter(tool => hidden.includes(tool));
}

/**
 * The course weeks whose day-before section reminder also goes to students,
 * and what was wrong with the stored field.
 */
export interface ResolvedStudentDayBeforeReminderWeeks {
  /** The 1-based course weeks, ascending and without repeats. */
  weeks: readonly number[];
  /**
   * One message per stored value that was ignored. Empty when the field is
   * absent or entirely valid.
   */
  problems: readonly string[];
}

/**
 * The course weeks in which students get the day-before section reminder.
 *
 * @param args - Inputs.
 * @param args.course - The course document. The field is typed `unknown`
 *   because course converters read without validating. Absent resolves to
 *   {@link STUDENT_DAY_BEFORE_REMINDER_WEEKS_DEFAULT}. A value that is not a
 *   list, or a non-empty list with no valid week, also resolves to the default;
 *   otherwise invalid entries are dropped. Each ignored value is reported in
 *   `problems`.
 * @returns The weeks and the problems found.
 * @throws Never.
 */
export function resolveStudentDayBeforeReminderWeeks({
  course,
}: {
  course: {studentDayBeforeReminderWeeks?: unknown} | null | undefined;
}): ResolvedStudentDayBeforeReminderWeeks {
  const stored = course?.studentDayBeforeReminderWeeks;
  if (stored === undefined) {
    return {weeks: STUDENT_DAY_BEFORE_REMINDER_WEEKS_DEFAULT, problems: []};
  }
  if (!Array.isArray(stored)) {
    return {
      weeks: STUDENT_DAY_BEFORE_REMINDER_WEEKS_DEFAULT,
      problems: [
        `studentDayBeforeReminderWeeks is not a list: ${JSON.stringify(stored)}`,
      ],
    };
  }
  const weeks = new Set<number>();
  const problems: string[] = [];
  for (const entry of stored) {
    const parsed = StudentDayBeforeReminderWeekSchema.safeParse(entry);
    if (parsed.success) {
      weeks.add(parsed.data);
    } else {
      problems.push(
        `studentDayBeforeReminderWeeks entry is not a course week: ` +
          JSON.stringify(entry),
      );
    }
  }
  if (weeks.size === 0 && stored.length > 0) {
    return {weeks: STUDENT_DAY_BEFORE_REMINDER_WEEKS_DEFAULT, problems};
  }
  return {weeks: [...weeks].sort((a, b) => a - b), problems};
}

/**
 * Whether the SL/mentor certificate-name capture window is open at `now`.
 *
 * The window is the closed interval `[open, close]` — both boundaries are
 * inclusive. An unset boundary is treated as unbounded on that side, so a
 * course with neither timestamp set is always open (the legacy default before
 * these fields existed). A boundary that fails to parse as a date is ignored
 * (treated as unset) so malformed config fails open rather than locking the
 * flow.
 *
 * @param args - Inputs.
 * @param args.course - The course document (only the two
 *   `teacherCertificateName*Timestamp` fields are read).
 * @param args.now - The current time to test against the window.
 * @returns `true` when `now` is within `[open, close]`, else `false`.
 * @throws Never.
 */
export function isTeacherCertificateNameWindowOpen({
  course,
  now,
}: {
  course: Pick<
    CourseDocument,
    | 'teacherCertificateNameOpenTimestamp'
    | 'teacherCertificateNameCloseTimestamp'
  >;
  now: Date;
}): boolean {
  const nowMs = now.getTime();
  const openMs = course.teacherCertificateNameOpenTimestamp
    ? Date.parse(course.teacherCertificateNameOpenTimestamp)
    : Number.NaN;
  const closeMs = course.teacherCertificateNameCloseTimestamp
    ? Date.parse(course.teacherCertificateNameCloseTimestamp)
    : Number.NaN;
  if (!Number.isNaN(openMs) && nowMs < openMs) {
    return false;
  }
  if (!Number.isNaN(closeMs) && nowMs > closeMs) {
    return false;
  }
  return true;
}

/**
 * Shutdown status of a course-gated, cost-bearing AI feature (the SimTeach
 * practice experience and the coding buddy), independent of who is viewing:
 *
 * - `disabled` — the feature's master switch is off; it is absent from the
 *   course for everyone.
 * - `ended` — the feature is enabled but the current time is at or after its
 *   scheduled shutdown instant, so it is closed to everyone below admin.
 * - `open` — the feature is enabled and (if it has one) before its shutdown
 *   instant; available to everyone with route access.
 */
export type ScheduledShutdownStatus = 'disabled' | 'ended' | 'open';

/**
 * The {@link ScheduledShutdownStatus} of a feature, independent of the viewer's
 * role (combine with role via {@link canAccessScheduledShutdownFeature}).
 *
 * The upper boundary is closed: `ended` starts exactly at `endTimestamp`, so
 * the open window is the half-open interval `(-∞, endTimestamp)`. An absent or
 * unparseable `endTimestamp` means no shutdown.
 *
 * @param args - Inputs.
 * @param args.enabled - Resolved master switch for the course.
 * @param args.endTimestamp - ISO 8601 shutdown instant; undefined/unparseable =
 *   never ends.
 * @param args.now - Time to test; pass server time where clock-tampering
 *   matters.
 * @returns The shutdown status.
 * @throws Never.
 */
export function getScheduledShutdownStatus({
  enabled,
  endTimestamp,
  now,
}: {
  enabled: boolean;
  endTimestamp?: string;
  now: Date;
}): ScheduledShutdownStatus {
  if (!enabled) {
    return 'disabled';
  }
  const endMs = endTimestamp ? Date.parse(endTimestamp) : Number.NaN;
  if (!Number.isNaN(endMs) && now.getTime() >= endMs) {
    return 'ended';
  }
  return 'open';
}

/**
 * Whether a viewer may access a scheduled-shutdown AI feature now: false when
 * disabled (even for admins), open to all before the shutdown, admin-only
 * at/after it. Window semantics are {@link getScheduledShutdownStatus}; callers
 * resolve the `enabled` default themselves.
 *
 * @param args - Inputs.
 * @param args.enabled - Resolved master switch for the course.
 * @param args.endTimestamp - ISO 8601 shutdown instant, or undefined for none.
 * @param args.now - Time to test; pass server time where clock-tampering
 *   matters.
 * @param args.isAdmin - Whether the viewer is admin or above (kept in after
 *   shutdown).
 * @returns Whether access is allowed now.
 * @throws Never.
 */
export function canAccessScheduledShutdownFeature({
  enabled,
  endTimestamp,
  now,
  isAdmin,
}: {
  enabled: boolean;
  endTimestamp?: string;
  now: Date;
  isAdmin: boolean;
}): boolean {
  const status = getScheduledShutdownStatus({enabled, endTimestamp, now});
  if (status === 'disabled') {
    return false;
  }
  if (status === 'ended') {
    return isAdmin;
  }
  return true;
}

// ============================================================================
// Paths
// ============================================================================

export const COURSE_COLLECTION = 'course' as const;

/**
 * The CIP-specific "public" courseId. This is a real course doc that is the
 * default landing context for unauthenticated visitors and the umbrella for
 * marketing surfaces; multiple webapp branches treat it specially (e.g.
 * `Profile`/`ViewOnlyProfile` show unregistered viewers as `STUDENT` here,
 * `RolesBadges` only renders for `cip*` or `public` courses, `CourseNavbar`
 * varies its layout under `'public'`).
 *
 * Centralizing the literal here lets every consumer share one canonical
 * source instead of scattering `=== 'public'` checks across the webapp.
 */
export const PUBLIC_COURSE_ID = 'public' as const;

export function getCourseCollectionPath(): string {
  return COURSE_COLLECTION;
}

export function getCourseDocumentPath({courseId}: {courseId: string}): string {
  return `${COURSE_COLLECTION}/${courseId}`;
}
